// export default IqResultPage;
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

function IqResultPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const [result, setResult] = useState(null);

    // ─── PERFORMANCE LEVEL (based on accuracy %) ───────────────────────────
    // Beginner   : below 30%
    // Average    : 30% – 59%
    // Good       : 60% – 84%
    // Excellent  : 85% and above
    const getPerformanceLevel = (accuracyPercent) => {
        if (accuracyPercent >= 85) return "Excellent";
        if (accuracyPercent >= 60) return "Good";
        if (accuracyPercent >= 30) return "Average";
        return "Beginner";
    };

    const getPerformanceColor = (level) => {
        switch (level) {
            case "Excellent": return "#19fd91";
            case "Good": return "#7ee787";
            case "Average": return "#ffd166";
            default: return "#ff9999"; // Beginner
        }
    };

    // ─── CLASSIFICATION COLOR (for the IQ classification label) ────────────
    // ✅ Matches the 7-tier table used by IqQuizPage.getIQClassification:
    // Very Superior / Superior / Above Average / Average / Below Average /
    // Borderline / Estimated Extremely Low
    const getClassificationColor = (classification = "") => {
        const c = classification.toLowerCase();
        if (c.includes("superior")) return "#19fd91";           // Very Superior, Superior
        if (c.includes("above average")) return "#7ee787";
        if (c.includes("below average") || c.includes("borderline")) return "#ffd166";
        if (c.includes("extremely low")) return "#ff9999";
        if (c === "average") return "#ffffff";
        return "#b4bebd";
    };

    useEffect(() => {
        if (location.state) {
            setResult(location.state);
        } else {
            navigate("/tests");
        }
    }, [location, navigate]);

    const theme = {
        bgMain: "var(--bg-main, #0a140f)",
        bgCard: "var(--bg-card, #111c17)",
        accent: "var(--accent, #19fd91)",
        textMain: "var(--text-main, #ffffff)",
        textMuted: "var(--text-muted, #b4bebd)",
        border: "var(--border, #233127)",
    };

    if (!result) return null;

    const userAnswers = result.userAnswers || {};
    const questions = result.questions || [];

    const rawScore = result.score ?? result.correct ?? 0;
    const totalQuestions = result.total ?? questions.length ?? 30;
    const correct = result.correct ?? rawScore;
    const wrong = result.wrong ?? (totalQuestions - rawScore);

    // ✅ Use the SAME iq/classification computed on the quiz page — never
    // recalculate here, so both pages always agree on one number.
    const iqScore = result.iq ?? "N/A";
    const classification = result.classification ?? "Unknown";
    const classificationColor = getClassificationColor(classification);

    // ─── NEW: Accuracy + Performance Level report ──────────────────────────
    const accuracy = totalQuestions > 0 ? (correct / totalQuestions) * 100 : 0;
    const accuracyDisplay = accuracy.toFixed(1); // e.g. "23.3"
    const performanceLevel = getPerformanceLevel(accuracy);
    const performanceColor = getPerformanceColor(performanceLevel);

    const formattedDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });

    return (
        <div
            style={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                background: theme.bgMain,
                paddingTop: "120px",
                paddingBottom: "3rem",
                fontFamily: "'Inter', sans-serif",
            }}
        >
            <div
                style={{
                    background: theme.bgCard,
                    padding: "3rem 2.5rem",
                    width: "95%",
                    maxWidth: "600px",
                    borderRadius: "20px",
                    boxShadow: "0 6px 40px rgba(0,0,0,0.12)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1.5rem",
                    border: `1px solid ${theme.border}`,
                }}
            >
                <h1
                    style={{
                        color: theme.textMain,
                        fontSize: "1.8rem",
                        fontWeight: "700",
                        margin: 0,
                    }}
                >
                    IQ Test Result
                </h1>
                <div
                    style={{
                        color: theme.textMuted,
                        fontSize: "1rem",
                        marginBottom: "1rem",
                    }}
                >
                    {formattedDate}
                </div>
                <div
                    style={{
                        width: "100%",
                        height: "1px",
                        background: theme.border,
                        marginBottom: "1rem",
                    }}
                ></div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "0.5rem",
                    }}
                >
                    <span
                        style={{
                            color: theme.textMuted,
                            fontSize: "1.2rem",
                            fontWeight: "500",
                        }}
                    >
                       Estimated IQ Score
                    </span>
                    <span
                        style={{
                            color: theme.textMain,
                            fontSize: "4rem",
                            fontWeight: "800",
                            lineHeight: "1",
                        }}
                    >
                        {iqScore}
                    </span>
                    <span
                        style={{
                            color: classificationColor,
                            fontSize: "1.5rem",
                            fontWeight: "600",
                            marginTop: "0.5rem",
                            textAlign: "center",
                        }}
                    >
                        {classification}
                    </span>

                    {/* ─── Disclaimer ──────────────────────────────────────── */}
                    <span
                        style={{
                            color: theme.textMuted,
                            fontSize: "0.8rem",
                            fontStyle: "italic",
                            textAlign: "center",
                            marginTop: "0.6rem",
                            maxWidth: "420px",
                            lineHeight: "1.4",
                        }}
                    >
                        This is an estimated score based on this quiz and is not a clinically validated IQ measurement.
                    </span>
                </div>

                {/* ─── Honest, transparent report ────────────────────────────── */}
                <div
                    style={{
                        width: "100%",
                        marginTop: "1rem",
                        padding: "1.2rem",
                        borderRadius: "12px",
                        background: "#0f1a14",
                        border: `1px solid ${theme.border}`,
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.7rem",
                    }}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem" }}>
                        <span style={{ color: theme.textMuted }}>Raw Score</span>
                        <span style={{ color: theme.textMain, fontWeight: 600 }}>
                            {rawScore} / {totalQuestions}
                        </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem" }}>
                        <span style={{ color: theme.textMuted }}>Accuracy</span>
                        <span style={{ color: theme.textMain, fontWeight: 600 }}>
                            {accuracyDisplay}%
                        </span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.95rem" }}>
                        <span style={{ color: theme.textMuted }}>Performance Level</span>
                        <span style={{ color: performanceColor, fontWeight: 700 }}>
                            {performanceLevel}
                        </span>
                    </div>
                </div>

                <div
                    style={{
                        marginTop: "1rem",
                        display: "flex",
                        justifyContent: "center",
                        gap: "20px",
                        fontSize: "1rem",
                        fontWeight: "600",
                    }}
                >
                    <span style={{ color: "#19fd91" }}>✅ Correct: {correct}</span>
                    <span style={{ color: "#ff6b6b" }}>❌ Wrong: {wrong}</span>
                </div>

                <div style={{ width: "100%", marginTop: "2rem" }}>
                    <h3
                        style={{
                            color: theme.accent,
                            marginBottom: "1rem",
                            textAlign: "center",
                        }}
                    >
                        Answer Review
                    </h3>

                    {questions.map((q, index) => {
                        const userAnswer = userAnswers[index];
                        const isCorrect = userAnswer === q.answer;

                        return (
                            <div
                                key={index}
                                style={{
                                    background: "#0f1a14",
                                    border: `1px solid ${isCorrect ? "#19fd91" : "#ff6b6b"}`,
                                    borderRadius: "10px",
                                    padding: "1rem",
                                    marginBottom: "1rem",
                                }}
                            >
                                <div style={{ fontWeight: "600", marginBottom: "0.5rem" }}>
                                    Q{index + 1}. {q.question}
                                </div>

                                <div style={{ fontSize: "0.9rem" }}>
                                    Your Answer:{" "}
                                    <span
                                        style={{
                                            color: isCorrect ? "#19fd91" : "#ff6b6b",
                                            fontWeight: "600",
                                        }}
                                    >
                                        {userAnswer || "Not Answered"}
                                    </span>
                                </div>

                                {!isCorrect && (
                                    <div
                                        style={{
                                            fontSize: "0.9rem",
                                            color: "#19fd91",
                                            marginTop: "0.3rem",
                                        }}
                                    >
                                        Correct Answer: {q.answer}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div
                    style={{
                        marginTop: "1rem",
                        fontSize: "0.95rem",
                        color: theme.accent,
                        textAlign: "center",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                    }}
                >
                    ✅ Result saved to your profile!
                </div>
                <button
                    onClick={() => navigate("/profile")}
                    style={{
                        backgroundColor: "transparent",
                        color: theme.textMuted,
                        border: `1px solid ${theme.textMuted}`,
                        padding: "0.8rem 2.5rem",
                        fontSize: "1rem",
                        fontWeight: "600",
                        borderRadius: "12px",
                        cursor: "pointer",
                        marginTop: "1.5rem",
                        transition: "all 0.2s ease",
                    }}
                    onMouseOver={(e) => {
                        e.target.style.borderColor = theme.accent;
                        e.target.style.color = theme.accent;
                    }}
                    onMouseOut={(e) => {
                        e.target.style.borderColor = theme.textMuted;
                        e.target.style.color = theme.textMuted;
                    }}
                >
                    Go To Profile
                </button>
            </div>
        </div>
    );
}

export default IqResultPage;